import { useState } from 'react'
import { api } from '../lib/api'
import { useToast } from './Toast'
import { ModalShell } from './ui/ModalShell'

interface Props {
  comida_id: number
  comida_label: string
  onClose: () => void
  onSuccess: () => void
}

const MOTIVO_MIN = 5
const MOTIVO_MAX = 500
const NOTAS_MAX = 2000

export default function CancelarComidaDialog({
  comida_id,
  comida_label,
  onClose,
  onSuccess,
}: Props) {
  const { showToast } = useToast()
  const [motivo, setMotivo] = useState('')
  const [notas, setNotas] = useState('')
  const [loading, setLoading] = useState(false)

  const motivoTrim = motivo.trim()
  const motivoValido = motivoTrim.length >= MOTIVO_MIN && motivoTrim.length <= MOTIVO_MAX
  const notasValidas = notas.length <= NOTAS_MAX

  async function handleSubmit() {
    if (!motivoValido) {
      showToast(`El motivo debe tener entre ${MOTIVO_MIN} y ${MOTIVO_MAX} caracteres.`, 'warning')
      return
    }
    if (!notasValidas) {
      showToast(`Las notas no pueden superar ${NOTAS_MAX} caracteres.`, 'warning')
      return
    }

    setLoading(true)
    try {
      await api.post(`/admin/comida/${comida_id}/cancelar`, {
        motivo: motivoTrim,
        notas: notas.trim() ? notas.trim() : null,
      })
      showToast('Comida cancelada correctamente.', 'success')
      onSuccess()
      onClose()
    } catch (err: any) {
      const status = err.response?.status
      const detail = err.response?.data?.detail
      if (status === 404) {
        showToast('Esta comida ya no existe. Refrescando lista…', 'error')
        onSuccess()
        onClose()
      } else if (status === 409) {
        showToast(detail || 'La comida está en un estado que no permite cancelación.', 'error')
      } else if (status === 500) {
        showToast('Error del servidor al cancelar. Reintentá en un momento.', 'error')
      } else {
        showToast(`Error: ${detail || err.message}`, 'error')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalShell
      title="⛔ Cancelar comida (admin)"
      subtitle={comida_label}
      onClose={onClose}
      closeDisabled={loading}
      maxWidth={480}
    >
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded mb-4">
          Esta acción es auditable y queda registrada con tu usuario. Solo se puede cancelar si la
          comida aún no fue entregada.
        </p>

        <label className="block text-sm font-semibold mb-1">
          Motivo <span className="text-red-600">*</span>
        </label>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          maxLength={MOTIVO_MAX}
          disabled={loading}
          className="w-full p-2 border rounded text-sm"
          placeholder="Ej: paciente cambió de habitación, alta médica, indicación mal capturada…"
        />
        <p className="text-xs text-gray-500 mt-1 mb-3">
          {motivoTrim.length}/{MOTIVO_MAX} (mínimo {MOTIVO_MIN})
        </p>

        <label className="block text-sm font-semibold mb-1">Notas (opcional)</label>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          rows={3}
          maxLength={NOTAS_MAX}
          disabled={loading}
          className="w-full p-2 border rounded text-sm"
          placeholder="Detalle libre para el log de auditoría."
        />
        <p className="text-xs text-gray-500 mt-1 mb-4">
          {notas.length}/{NOTAS_MAX}
        </p>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 border border-gray-300 py-2 rounded text-sm disabled:opacity-50"
          >
            Volver
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !motivoValido}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
          >
            {loading ? 'Cancelando…' : 'Cancelar comida'}
          </button>
        </div>
    </ModalShell>
  )
}
